// NoneBooksMLMIncomeCredits.jsx
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import BookEarningCalculator from "../learning/BookEarningCalculator";
import "../learning/styles/BookShelfListComponent.css";
import "../learning/styles/ThinkleticsTables.css";
import adimage1 from '../../assets/pageimages/roomanywhere/raindeximg1.png';
import adimage2 from '../../assets/navlogo.png';

const NoneBooksMLMIncomeCredits = () => {
  const authState = useSelector((state) => state.authReducer);
  const { token, user, isLoggedIn } = authState || {};
  const userId = user?._id;
  const myUserCode = user?.think5whyscode;

  const [fetchData] = useFetch();

  const [mlmBooks, setMlmBooks] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);

  // ─── Direct Referrals ───────────────────────────────
  const [directReferrals, setDirectReferrals] = useState([]);
  const [loadingDirect, setLoadingDirect] = useState(false);

  // ─── Down-the-line Referrals ─────────────────────────
  const [downlineReferrals, setDownlineReferrals] = useState([]);
  const [loadingDownline, setLoadingDownline] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("Lifetime");

  const periodOptions = [
    "Lifetime",
    "Today",
    "Yesterday",
    "Current Week",
    "Current Month",
    "Current Quarter",
    "Current Year",
    "Custom Date Range",
  ];

  // Fetch only MLM books
  const fetchMyMLMBooks = useCallback(() => {
    if (!isLoggedIn || !token) return;

    const config = {
      url: "/mybooks",
      method: "get",
      headers: { Authorization: token },
    };

    fetchData(config, { showSuccessToast: false })
      .then((res) => {
        if (res?.mybooks && Array.isArray(res.mybooks)) {
          const mlmOnly = res.mybooks.filter(
            (b) => b.buyingMethod === "MLM"
          );
          setMlmBooks(mlmOnly);
        }
      })
      .catch((err) => console.error("Failed to load MLM books:", err));
  }, [token, isLoggedIn, fetchData]);

  useEffect(() => {
    fetchMyMLMBooks();
  }, [fetchMyMLMBooks]);

  // ─── Fetch Direct Referrals ──────────────────────────
  const fetchDirectReferrals = useCallback(
    (bookId, userCode) => {
      if (!bookId || !userCode) return;

      setLoadingDirect(true);
      const config = {
        url: "/mybooks/mydirectreferralslist",
        method: "post",
        headers: { Authorization: token },
        data: {
          bookId,
          advisorCode: userCode,
          userCode,
        },
      };

      fetchData(config)
        .then((res) => {
          if (res?.referralsList?.length >= 0) {
            setDirectReferrals(res.referralsList);
          } else {
            setDirectReferrals([]);
          }
        })
        .catch((err) => {
          console.error("Direct referrals fetch failed:", err);
          setDirectReferrals([]);
        })
        .finally(() => setLoadingDirect(false));
    },
    [token, fetchData]
  );

  // ─── Fetch Down-the-line Referrals ───────────────────
  const fetchDownlineReferrals = useCallback(
    (bookId, userCode) => {
      if (!bookId || !userCode) return;

      setLoadingDownline(true);
      const config = {
        url: "/mlmchart/downthelinereferralslist",
        method: "post",
        headers: { Authorization: token },
        data: {
          bookId,
          userCode,
          userId,
        },
      };

      fetchData(config, { showSuccessToast: false })
        .then((res) => {
          if (res?.referralsList) {
            setDownlineReferrals(res.referralsList);
          } else {
            setDownlineReferrals([]);
          }
        })
        .catch((err) => {
          console.error("Downline fetch failed:", err);
          setDownlineReferrals([]);
        })
        .finally(() => setLoadingDownline(false));
    },
    [token, userId, fetchData]
  );

  const handleSelectBook = (book) => {
    const isAlreadySelected = selectedBook?.bookId === book.bookId;

    if (isAlreadySelected) {
      setSelectedBook(null);
      setDirectReferrals([]);
      setDownlineReferrals([]);
    } else {
      setSelectedBook(book);
      fetchDirectReferrals(book.bookId, book.userCode);
      fetchDownlineReferrals(book.bookId, book.userCode);
    }
  };

  // Filtered & Aggregated downline data
  const { filteredDownline, levelSummary } = useMemo(() => {
    if (!downlineReferrals.length) {
      return { filteredDownline: [], levelSummary: {} };
    }

    const now = new Date();
    let start = null;
    let end = null;

    switch (selectedPeriod) {
      case "Today":
        start = new Date(now.setHours(0, 0, 0, 0));
        end = new Date(now.setHours(23, 59, 59, 999));
        break;
      case "Lifetime":
        break;
      // ... you can add more cases (Yesterday, Week, Month, etc.)
      default:
        break;
    }

    const filtered = downlineReferrals.filter((item) => {
      if (!start) return true;
      const d = new Date(item.bookBuyDate);
      return d >= start && (!end || d <= end);
    });

    const summary = {};
    filtered.forEach((item) => {
      const lvl = item.matchedLevel;
      if (!summary[lvl]) {
        summary[lvl] = { count: 0, total: 0 };
      }
      summary[lvl].count += 1;
      summary[lvl].total += item.commissionEarned || 0;
    });

    return { filteredDownline: filtered, levelSummary: summary };
  }, [downlineReferrals, selectedPeriod]);

  const totalCount = Object.values(levelSummary).reduce(
    (sum, v) => sum + v.count,
    0
  );
  const totalIncome = Object.values(levelSummary).reduce(
    (sum, v) => sum + v.total,
    0
  );

  if (!isLoggedIn) {
    return <div className="text-center p-8">Please log in first</div>;
  }

  return (
    <div className="min-h-screen bg-gray-950 [&_p]:text-gray-100 text-gray-100 p-4">
      <h1 className="text-lg lg:text-xl font-bold mb-6 text-center">
        ValueShare Products Income Credits
      </h1>
      <div
        className="mt-4 p-3 border-2 border-dashed border-gray-500 rounded-lg bg-gray-900 text-gray-300 cursor-pointer hover:bg-gray-800 transition-all flex items-center gap-3"
        onClick={() => navigate("/roomanywhere")}
        >
            <div className="flex-shrink-0 w-20 h-20 rounded overflow-hidden bg-gray-700">
                <img
                src={adimage1} 
                alt="Ad"
                className="w-full h-full object-cover"
                />
            </div>
            <div className="flex flex-col text-left">
                <p className="text-xs uppercase text-gray-400 mb-1">Think5Whys Ad</p>
                <p className="font-semibold text-lg"><span className="text-yellow-200">RoomAnywhere</span>: Make Indoor and Outdoor Room (Patent Applied)</p>
                <p className="text-sm mt-1">Click to see more!</p>
            </div>
      </div>  
      {mlmBooks.length === 0 ? (
        <div className="text-center text-yellow-400 text-xl">
          No books purchased via MLM / ValueShare program
        </div>
      ) : (
        <div className="space-y-3 max-w-4xl mx-auto">
          {/* Book List – clickable titles */}
          {mlmBooks
            .sort((a, b) => new Date(b.purchaseDate) - new Date(a.purchaseDate))
            .map((book) => {
              const isSelected = selectedBook?.bookId === book.bookId;

              return (
                <div
                  key={book.bookId}
                  onClick={() => handleSelectBook(book)}
                  className={`p-4 rounded-lg border transition-all cursor-pointer ${
                    isSelected
                      ? "bg-indigo-950 border-indigo-400 shadow-lg"
                      : "bg-gray-900 border-gray-700 hover:border-indigo-600"
                  }`}
                >
                  <div className="flex justify-between items-center">
                    <h2 className="text-lg font-semibold text-yellow-200">
                      {book.isCollection && (
                        <span className="text-yellow-400 mr-2">Collection:</span>
                      )}
                      {book.bookTitle}
                    </h2>
                    <span className="text-sm text-gray-400">
                      {new Date(book.purchaseDate).toLocaleDateString()}
                    </span>
                  </div>

                  {/* Details – shown only when selected */}
                  {isSelected && (
                    <div className="mt-6 space-y-8 animate-fade-in">
                      {/* Referrer */}
                      <div>
                        <h3 className="text-lg font-medium text-yellow-300 mb-2">
                          My Referrer
                        </h3>
                        <p className="text-gray-300">
                          {book.advisorName || "—"} ({book.advisorCode || "—"})
                        </p>
                      </div>

                      {/* Direct Referrals */}
                      <div>
                        <h3 className="text-lg font-medium text-cyan-300 mb-2">
                          Direct Referrals (Level 1)
                        </h3>
                        {loadingDirect ? (
                          <p>Loading direct referrals...</p>
                        ) : directReferrals.length === 0 ? (
                          <p className="text-gray-500 italic">
                            No direct referrals yet
                          </p>
                        ) : (
                          <div className="grid gap-2">
                            {directReferrals.map((ref, i) => {
                                if (ref.userCode === myUserCode) return null;

                                return (
                                    <div key={i} className="flex flex-row gap-3 p-3 rounded border border-gray-700 text-sm">
                                        <div className="font-bold bg-cyan-700 border border-gray-400 px-2">{i}</div>
                                        <div>
                                            <div className="font-medium">{ref.userName} ({ref.userCode})</div>
                                            <div className="text-gray-400 text-xs mt-1">
                                                {ref.purchaseDate ? new Date(ref.purchaseDate).toLocaleString() : "—"}
                                            </div>
                                            <div className="text-green-400 mt-1">
                                                ₹ {(ref.salesPrice || 0).toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                );
                                })}
                          </div>
                        )}
                      </div>

                      {/* Down-the-line Income */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-lg font-medium text-purple-300">
                            ValueShare / Downline Income
                          </h3>

                          <select
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                            className="bg-gray-800 border border-gray-600 rounded px-3 py-1 text-sm"
                          >
                            {periodOptions.map((p) => (
                              <option key={p} value={p}>
                                {p}
                              </option>
                            ))}
                          </select>
                        </div>

                        {loadingDownline ? (
                          <p>Loading downline data...</p>
                        ) : Object.keys(levelSummary).length === 0 ? (
                          <p className="text-gray-500 italic">
                            No downline activity yet
                          </p>
                        ) : (
                          <>
                            <div className="overflow-x-auto">
                              <table className="min-w-full text-sm">
                                <thead className="bg-gray-800">
                                  <tr>
                                    <th className="p-3 text-left">Level</th>
                                    <th className="p-3 text-right">Count</th>
                                    <th className="p-3 text-right">Income (₹)</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(levelSummary)
                                    .sort(([a], [b]) => Number(a) - Number(b))
                                    .map(([level, data]) => (
                                      <tr
                                        key={level}
                                        className="border-b border-gray-700"
                                      >
                                        <td className="p-3">Level {level}</td>
                                        <td className="p-3 text-right">
                                          {data.count}
                                        </td>
                                        <td className="p-3 text-right text-green-400">
                                          {data.total.toFixed(2)}
                                        </td>
                                      </tr>
                                    ))}

                                  <tr className="font-bold bg-gray-800">
                                    <td className="p-3">Total</td>
                                    <td className="p-3 text-right">
                                      {totalCount}
                                    </td>
                                    <td className="p-3 text-right text-green-300">
                                      {totalIncome.toFixed(2)}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>

                            {/* Optional: show calculator */}
                            <div className="mt-6">
                               <h3 className="text-lg font-medium text-purple-300">
                                Probable Earning Calculator (Taking 5 Referrals at Each level as example and max 8 level allowed)
                               </h3>
                              <BookEarningCalculator param={selectedBook.salesPrice} />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}
        <div
            className="mt-4 p-3 border-2 border-dashed border-gray-500 rounded-lg bg-gray-900 text-gray-300 cursor-pointer hover:bg-gray-800 transition-all flex items-center gap-3"
            onClick={() => window.open("https://www.think5whys.com", "_blank")}
            >
            <div className="flex-shrink-0 w-16 h-16 bg-gray-700 rounded overflow-hidden">
                <img
                    src={adimage2}
                    alt="Ad"
                    className="w-full h-full object-cover"
                />
                </div>

                <div className="flex flex-col text-left">
                <p className="text-xs uppercase text-gray-400 mb-1">Google Ad</p>
                <p className="font-semibold text-lg">Think5Whys: Your Platform for Transformation</p>
                <p className="text-sm mt-1">Click to see more!</p>
            </div>
        </div> 
    </div>
  );
};

export default NoneBooksMLMIncomeCredits;